import {useMemo} from "react";
import useTransferInfoGet from "./useTransferInfoGet";
import useEventGet from "../../events/hooks/useEventGet";
import useEventCityGet from "../../eventCities/hooks/useEventCityGet";
import useCityGet from "../../cities/hooks/useCityGet";
import useDepartmentGet from "./useDepartmentGet";
import useTransportLocationGetByID from "./useTransportLocationGetByID";
import useTransferParentInfoGet from "./useTransferParentInfoGet";
import Transfer from "../types/Transfer";
import EventCity from "../../eventCities/types/EventCity";
import Event from "../../events/types/Event";
import City from "../../cities/types/City";
import Department from "../types/Department";
import TransportLocation from "../types/TransportLocation";
import {TransferCommonDescription} from "../types/TransferCommonDescription";
import TransferRoute from "../types/TransferRoute";
import {extractIdFromPath} from "../utils/extractIdFromPath";
import useTransferRoute from "./useTransferRoute";

export interface UseTransferDetailsGetResult {
    transferInfo: Transfer | undefined;
    cityEventInfo: EventCity | undefined;
    eventInfo: Event | undefined;
    cityInfo: City | undefined;
    departmentData: Department | undefined;
    locationFrom: TransportLocation | undefined;
    locationTo: TransportLocation | undefined;
    parentTransfer: TransferCommonDescription | undefined;
    route: TransferRoute | null;
    isLoading: boolean;
    readyToRender: boolean;
}




export default function useTransferDetailsGet(uuid: string): UseTransferDetailsGetResult {
    const transferInfoQuery = useTransferInfoGet(uuid);
    const {data: transferInfo, isLoading: isLoadingTransferInfo} = transferInfoQuery;

    const cityEventId = extractIdFromPath(transferInfo?.cityEvent);
    const cityEventInfoQuery = useEventCityGet(cityEventId, {execute: !isLoadingTransferInfo});
    const {data: cityEventInfo, isLoading: isLoadingCityEventInfo} = cityEventInfoQuery;

    const eventInfoQuery = useEventGet(transferInfo?.event ?? 0, {execute: !isLoadingTransferInfo});
    const {data: eventInfo} = eventInfoQuery;

    const cityInfoQuery = useCityGet(cityEventInfo?.city ?? 'cities/0', {execute: !!cityEventInfo?.city && !isLoadingTransferInfo});
    const {data: cityInfo} = cityInfoQuery;

    const departmentId = extractIdFromPath(transferInfo?.passengerDepartment);
    const departmentQuery = useDepartmentGet(departmentId, {execute: !isLoadingTransferInfo});
    const {data: departmentData} = departmentQuery;

    const locationFromId = extractIdFromPath(transferInfo?.transportLocationFrom);
    const locationFromQuery = useTransportLocationGetByID(locationFromId, {execute: !isLoadingTransferInfo});
    const {data: locationFrom} = locationFromQuery;

    const locationToId = extractIdFromPath(transferInfo?.transportLocationTo);
    const locationToQuery = useTransportLocationGetByID(locationToId, {execute: !isLoadingTransferInfo});
    const {data: locationTo} = locationToQuery;

    const parentTransferQuery = useTransferParentInfoGet(transferInfo?.transferCommonDescription ?? 'transfer_common_descriptions/0', {execute: !!transferInfo?.transferCommonDescription && !isLoadingTransferInfo});
    const {data: parentTransfer} = parentTransferQuery;

    // Validate all required values are present
    const hasRequiredRouteData = !!cityInfo?.id && !!locationFrom && !!locationTo && !isLoadingTransferInfo;

    // Get transfer routes based on direction
    const {route} = useTransferRoute({cityId: cityInfo?.id, locationFrom: locationFrom, locationTo: locationTo, execute: hasRequiredRouteData,});

    const isLoading = useMemo(() => {
        return (isLoadingTransferInfo || isLoadingCityEventInfo);
    }, [isLoadingTransferInfo, isLoadingCityEventInfo]);

    const readyToRender = useMemo(() => {
        return (
            transferInfo !== undefined &&
            cityEventInfo !== undefined &&
            eventInfo !== undefined &&
            cityInfo !== undefined &&
            departmentData !== undefined &&
            locationFrom !== undefined &&
            locationTo !== undefined &&
            parentTransfer !== undefined &&
            !isLoading
        );
    }, [transferInfo, cityEventInfo, eventInfo, cityInfo,departmentData, locationFrom, locationTo, parentTransfer, isLoading]);

    return {
        transferInfo,
        cityEventInfo,
        eventInfo,
        cityInfo,
        departmentData,
        locationFrom,
        locationTo,
        parentTransfer,
        route,
        isLoading,
        readyToRender
    };
}

